import { useMemo } from 'react';

import { Col, Divider, Row, Tabs } from 'antd';
import { useParams } from 'react-router-dom';
import { CircularProgress, Title } from 'components';
import { ArticleOfferDetail, ArticleProcess, Timeline } from 'components/ArticleOffer';
import { ArticleOrderDetail } from 'components/ArticleOrder';
import { useGetCurrentUserQuery, useGetOfferQuery } from 'services';
import ProtectedRoute from 'components/ProtectRoute';
import AfterCopyright from 'components/AfterCopyright/AfterCopyright';
import { article_order_status } from 'constant/static';
import EncyclopediaUnitControl from 'components/AfterCopyright/EncyclopediaUnit/EncyclopediaUnitControl';
import PublishManagement from 'components/AfterCopyright/PublishManagement/PublishManagement';
import PublishManagementControl from 'components/AfterCopyright/PublishManagement/PublishManagementControl/PublishManagementControl';
import EncyclopediaManagementUnit from 'components/AfterCopyright/EncylopediaManagementUnit/EncyclopediaManagementUnit';
import Publisment from 'components/AfterCopyright/Publisment';
import RejectedContentItem from 'components/AfterCopyright/RejectedContentDetail';
import { useArticleTitleEditPermission, useUserHasPermission } from 'hooks';
import { isDisposalPaymentFeatureEnabled } from 'constant';
import { isSavingContractAvailable } from 'components/ArticleOrder/SavingContractAction/savingContractVisibility';

import useHandleProcess from './useHandleProcess';
import ArticleHeaderGroup from './ArticleHeaderGroup';
import { useArticleDetailContext } from './ArticleDetailContext';

const { TabPane } = Tabs;

export const decidableCommitteeStrings = {
  eu: 'eu',
  pub: 'pub',
};

const ArticleDetail = () => {
  const { id } = useParams();
  const { data: offer, isLoading } = useGetOfferQuery(id);
  const { data: current_user } = useGetCurrentUserQuery();

  const { handleUserIsOnCommittee, isArticleSupervisor } = useUserHasPermission();

  /**
   * telif sonrasi asamalarini maddenin ait oldugu komite ve telif sonrasi komitelerin hepsinin gormesi kosulunu kontrol eder
   */
  const canSeeAfterCopyright = useMemo(() => {
    const isUserOnOwnerCommittee = handleUserIsOnCommittee(offer && offer.ownerCommittee?.id);
    const isUserArticleSupervisor = isArticleSupervisor(offer && offer.articleSupervisor?.id);
    return isUserOnOwnerCommittee || current_user.canSeeAfterCopyright || isUserArticleSupervisor;
  }, [current_user, handleUserIsOnCommittee, offer, isArticleSupervisor]);

  const { process, handleStep } = useHandleProcess({
    offerStep: offer && offer?.step,
    canSeeAfterCopyright,
  });
  const { timelineVisible } = useArticleDetailContext();

  /**
   * Madde adı düzenleme yetkisi (statü × rol matrisi) — kuralın tek kaynağı hook'tur, bkz.
   * `docs/article-title-edit.md`. Erken `return`'ün üstünde çağrılmak zorundadır (hook sırası).
   */
  const { canEditTitle } = useArticleTitleEditPermission(offer);

  /**
   *
   * @param {string} committee 'eu' | 'pub'
   * @returns {boolean}
   * @description Karar asamasindaki komitenin ansiklopedi birimi mi yoksa yayin mudurlugu mu oldugunu kontrol eder ve boolean dondurur
   * onaylandi, reddedildi, ansiklopedi birimi kontrol, ansiklopedi birimi görüş istenildi, yayin mudurlugu genel kontrol, ansiklopedi yonetim kurulu kontrol durumlarinda offer.decidableCommittee field render olmaz.
   * @example isDecideableCommitteePubOrEu('eu') // true
   * @example isDecideableCommitteePubOrEu('pub') // true
   * @example isDecideableCommitteePubOrEu(undefined) // false
   */
  function isDecideableCommitteePubOrEu(committee) {
    if (!offer) return false;
    return offer.decidableCommittee === committee;
  }
  if (!offer || isLoading) {
    return <CircularProgress />;
  }

  return (
    <ProtectedRoute user={offer.authors.map((author) => author.author.id)}>
      <Title>{`${offer.title + ' - Detay'}`}</Title>
      {/* Madde Başlığı ve Hareket Dokumu ve Madde Gruplama alanlarini render eder  */}
      <ArticleHeaderGroup
        title={offer && offer.title}
        isShowEditButton={canEditTitle}
        isShowArticleGroup={current_user.isAdmin || current_user.isOrderUnit}
        offerId={offer && offer.id}
        isView={offer && offer.isView}
        isShowTimelineButton={current_user.isAdmin || current_user.isOrderUnit}
        isForeignAuthorProcess={offer?.hasForeignAuthor}
        autoAddArticle={{
          offerId: offer?.['@id'],
          offerTitle: offer?.title,
          offerNumericId: offer?.id,
          offerStatus: offer?.status,
          offerStep: offer?.step,
        }}
        isShowPaymentInfoButton={
          isDisposalPaymentFeatureEnabled && (current_user.isAdmin || current_user.isOrderUnit) && isSavingContractAvailable(offer)
        }
        offer={offer}
      />

      <Divider className="gx-mt-0 gx-mb-4" />

      {timelineVisible ? (
        <Timeline id={offer.id} />
      ) : (
        <>
          {canSeeAfterCopyright && (
            <Row
              justify="center"
              gutter={3}
            >
              <Col span={18}>
                <ArticleProcess
                  offerStep={offer?.step}
                  process={process}
                  setProcess={handleStep}
                />
              </Col>
            </Row>
          )}

          <Tabs
            animated
            centered
            type="card"
            activeKey={process.toString()}
            tabBarStyle={{ display: 'none' }}
            destroyInactiveTabPane
          >
            <TabPane key="1">
              <ArticleOfferDetail
                offer={offer}
                process={process}
              />
            </TabPane>
            <TabPane key="2">
              <ArticleOrderDetail
                offer={offer}
                process={process}
              />
            </TabPane>
            <TabPane key="3">
              {/* Telif Sonrası Süreci */}
              {isDecideableCommitteePubOrEu(decidableCommitteeStrings.eu) && <AfterCopyright offer={offer} />}
              {/* Yayın Mudurlugu Kontrol Süreci */}
              {isDecideableCommitteePubOrEu(decidableCommitteeStrings.pub) && <PublishManagement offer={offer} />}
              {/* Ansiklopedi Birimi Kontrol */}
              {[
                article_order_status['Ansiklopedi Birimi Kontrol'],
                article_order_status['TS - İH Madde Kontrol'],
                article_order_status['TS - Madde Sorumlusu Kontrol'],
              ].includes(offer && offer.status) && <EncyclopediaUnitControl offer={offer} />}
              {/* Reddedildi */}
              {offer.status === article_order_status['Reddedildi'] && <RejectedContentItem offer={offer} />}
              {/* Yayın Mudurlugu Genel Kontrol */}
              {offer.status === article_order_status['Yayin Mudurlugu Genel Kontrol'] && <PublishManagementControl offer={offer} />}
              {/* Ansiklopedi Yonetim Kurulu Kontrol */}
              {offer.status === article_order_status['Ansiklopedi Yonetim Kurulu Kontrol'] && <EncyclopediaManagementUnit offer={offer} />}
            </TabPane>
            <TabPane key="4">
              <Publisment offer={offer} />
            </TabPane>
          </Tabs>
        </>
      )}
    </ProtectedRoute>
  );
};

export default ArticleDetail;
